import {
  type ClipboardEvent,
  type FC,
  type FocusEvent,
  type KeyboardEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

type LoginRegistrationOneTimePasswordInputProps = {
  defaultValue?: string
  formState: LoginRegistrationOneTimePasswordInputFormState
  hasError: boolean
  isSubmitted: boolean
  length?: number
}

import { clsx } from 'clsx'
import { useIntl } from 'react-intl'

import { type LoginRegistrationOneTimePasswordInputFormState } from '../../makeLoginRegistrationOneTimePasswordInputFormState'

import styles from './LoginRegistrationOneTimePasswordInput.module.css'

const DEFAULT_OTP_LENGTH = 6

const isKeyNumber = (key: string): boolean =>
  !Number.isNaN(Number.parseInt(key, 10))

export const LoginRegistrationOneTimePasswordInput: FC<
  PropsWithChildren<LoginRegistrationOneTimePasswordInputProps>
  // eslint-disable-next-line max-lines-per-function, max-statements -- the rule was disabled before a comment became required
> = ({
  children,
  defaultValue = '',
  formState,
  hasError = false,
  isSubmitted,
  length = DEFAULT_OTP_LENGTH,
}) => {
  const [isFirstFocus, setIsFirstFocus] = useState(true)
  const { model } = formState

  const intl = useIntl()

  const [otp, setOtp] = useState<string[]>(() => {
    const initialOtp = [...defaultValue]

    return Array.from({ length }, (_value, index) => initialOtp[index] || '')
  })

  const inputs = useRef<(HTMLInputElement | null)[]>(
    Array.from({ length }, () => null),
  )

  useEffect(() => {
    if (otp.join('') === '') {
      inputs.current[0]?.focus()
    }
  }, [otp])

  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (isSubmitted) {
      setShowError(true)
    }
  }, [isSubmitted])

  const focusNextInput = useCallback(
    (index: number) => {
      if (index + 1 < length) {
        inputs.current[index + 1]?.focus()
      } else if (index + 1 === length) {
        inputs.current[length - 1]?.focus()
      }
    },
    [length],
  )

  const triggerChange = (newValue: string): void => {
    formState.setValue(newValue, model, 'password')
  }

  const handleFocus = (): void => {
    if (otp.join('') === '' && isFirstFocus) {
      setIsFirstFocus(false)
      focusNextInput(-1)
    }
    setShowError(false)
  }

  const handleNumberKey = (key: string, index: number): void => {
    const newOtp = [...otp]
    newOtp[index] = key
    triggerChange(newOtp.join(''))
    setOtp(newOtp)
    focusNextInput(index)
    setShowError(false)
  }

  const handleBackspaceOrDelete = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ): void => {
    event.preventDefault()
    if (otp[index] === '') {
      if (index !== 0) {
        inputs.current[index - 1]?.focus()
      }
    } else {
      const newOtp = [...otp]
      newOtp[index] = ''
      triggerChange(newOtp.join(''))
      setOtp(newOtp)
      setTimeout(() => {
        inputs.current[index]?.focus()
      }, 0)
    }
    setShowError(false)
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ): void => {
    if (isKeyNumber(event.key)) {
      handleNumberKey(event.key, index)
    } else if (event.key === 'Backspace' || event.key === 'Delete') {
      handleBackspaceOrDelete(event, index)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const pasteData = [...event.clipboardData.getData('text')]
      .filter((inputKey) => isKeyNumber(inputKey))
      .slice(0, length)

    const newOtp = Array.from(
      { length },
      (_value, index) => pasteData[index] || '',
    )
    setOtp(newOtp)
    triggerChange(newOtp.join(''))
    const nextIndex = pasteData.length - 1
    if (nextIndex < length) {
      focusNextInput(nextIndex)
    }
    setShowError(false)
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setShowError(true)
    }
  }

  return (
    <div>
      <div className="flex justify-between">
        {otp.map((data, index) => (
          <div
            className={styles['otp-input-wrapper']}
            key={typeof data === 'string' ? `${data}-${index}` : index}
          >
            <input
              aria-label={intl.formatMessage(
                { id: 'login.otp.code.digits' },
                {
                  digitNumber: index + 1,
                },
              )}
              className={clsx(
                styles['otp-input-wrapper'],
                styles['otp-input'],
                showError && hasError && styles.error,
              )}
              data-qa-id={`otp-input-${index}`}
              maxLength={1}
              onBlur={handleBlur}
              onFocus={() => {
                handleFocus()
              }}
              onKeyDown={(event) => {
                handleKeyDown(event, index)
              }}
              onPaste={handlePaste}
              ref={(el) => {
                inputs.current[index] = el
              }}
              type="text"
              value={data}
            />
          </div>
        ))}
      </div>
      {showError ? children : null}
    </div>
  )
}
